import React, { useEffect, useState, useRef } from 'react'
import { Grid as Grid2, Box, Typography, Button, Divider } from '@mui/material'
import axios from 'axios'
import './News.css'
import iconEvent from '../../assets/images/icon_news.png'
import iconMarket from '../../assets/images/market.jpg'
import iconTips from '../../assets/images/tips.jpg'
import iconNews from '../../assets/images/news.jpg'
import { Link as MuiLink } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const VISIBLE_CARDS = 4
const SCROLL_INTERVAL = 1000000

type GroupTextItem = {
  id: number
  event: string
  date: string
  country: string
  area: string
}

const News: React.FC = () => {
  const [data, setNews] = useState<
    Array<{
      id: string | number
      name: string
      description?: string
      url?: string
      image?: string
      type?: string
    }>
  >([])

  const [startIndex, setStartIndex] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const [explore, setExplore] = useState<Record<string, GroupTextItem[]>>({})
  const [month, setMonth] = useState('')

  const stayTunedRef = useRef<HTMLDivElement>(null)
  const cardsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    axios
      .get('https://app.dfopsa.online/api/newss?populate[news][populate]=*')
      .then(res => {
        const raw = res.data.data
        const simplified = raw.map((item: any) => {
          const news = item.news
          if (news.is_visible == "true") {
          return {
            id: item.id,
            name: news.name,
            description: news.description,
            type: news.type_news,
            url: news.urlto,
            image: news.image?.[0]?.url
              ? `https://app.dfopsa.online${news.image[0].url}`
              : null,
          }
        }}).filter(Boolean);
        setNews(simplified)
      })
      .catch(err => console.error('Failed to fetch news:', err))
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `https://app.dfopsa.online/api/event?populate[countryes][populate]=*`
        )
        const currentMonthName = new Date().toLocaleString('en-US', {
          month: 'long',
        })
        setMonth(currentMonthName)
        const explore = res?.data?.data?.countryes || []
        const creatorInfoArray = explore.flatMap((item: any) =>
          item?.countryEvents
            .filter((event: any) =>
              event?.eventDate?.includes(currentMonthName)
            )
            .map((event: any) => {
              const [datePart] = event.eventDate.trim().split(/ (?=[^ ]*$)/)
              return {
                id: item.id,
                event: event.eventName,
                date: datePart,
                country: event.eventPlace,
                area: item.name,
              }
            })
        )
        const groupedByArea = creatorInfoArray.reduce((acc: any, curr: any) => {
          if (!acc[curr.area]) {
            acc[curr.area] = []
          }
          acc[curr.area].push(curr)
          return acc
        }, {})
        setExplore(groupedByArea)
      } catch (error) {
        console.error('Failed to fetch statistic data:', error)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    if (data.length <= VISIBLE_CARDS) return

    intervalRef.current = setInterval(() => {
      setStartIndex(prev => (prev + 1) % data.length)
    }, SCROLL_INTERVAL)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [data])

  const getVisibleItems = () => {
    const visible = []
    for (let i = 0; i < VISIBLE_CARDS; i++) {
      visible.push(data[(startIndex + i) % data.length])
    }
    return visible
  }

  const getIconByType = (type: string) => {
    switch (type) {
      case 'events':
        return { icon: iconEvent, bg: '#F68F57' }
      case 'market':
        return { icon: iconMarket, bg: '#016DB5' }
      case 'topic':
        return { icon: iconTips, bg: '#A020F0' }
      case 'news':
      default:
        return { icon: iconNews, bg: '#FBD350' }
    }
  }

  useEffect(() => {
    if (!stayTunedRef.current || !cardsRef.current) return

    gsap.fromTo(
      stayTunedRef.current,
      { opacity: 0, y: 40 },
      {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: stayTunedRef.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
        onComplete: () => {
          gsap.fromTo(
            cardsRef.current!.querySelectorAll('.carousel-item'),
            { opacity: 0, y: 60 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              stagger: 0.15,
              ease: 'power3.out',
              delay: 0.1,
              scrollTrigger: {
                trigger: cardsRef.current,
                start: 'top 80%',
                toggleActions: 'play none none reverse',
              },
            }
          )
        },
      }
    )
  }, [data])

  return (
    <Box id='news'>
      <Grid2
        className='news-carousel-container'
        container
        justifyContent='center'
        alignItems='start'
        spacing={{ xs: 1, md: 2 }}
        sx={{ pt: 15 }}
      >
        <Grid2
          className='nameSectionG flex'
          xs={12}
          sx={{ mb: 6 }}
          justifyContent='left'
          alignItems='start'
          spacing={{ xs: 1, md: 2 }}
        >
          <Box className='line l5'></Box>
          <Box className='nameSection' ref={stayTunedRef}>
            Stay tuned
          </Box>
        </Grid2>

        <Box className='carousel-container'>
          <Box className='carousel-track' ref={cardsRef}>
            {getVisibleItems().map((item, idx) => {
              if (!item) return null
              const { icon, bg } = getIconByType(item.type || 'news')

              return (
                <Box
                  sx={{
                    flex: {
                      xs: '0 0 100%',
                      sm: '0 0 50%',
                      md: '0 0 33.3%',
                      lg: '0 0 25%',
                    },
                    boxSizing: 'border-box',
                    padding: '10px',
                    textAlign: 'center',
                    minHeight: '347px !important',
                    overflow: 'hidden',
                  }}
                  className='carousel-item'
                  key={idx}
                >
                  <Box className='box' textAlign='center'>
                    <Box className='news-icon' sx={{ background: bg }}>
                      <img
                        style={{ borderRadius: '50%' }}
                        src={icon}
                        alt='News'
                        width={'70px'}
                      />
                      <Box sx={{ backgroundColor: bg }} className={'titleT'}>
                        {item.type?.toUpperCase() || 'NEWS'}
                      </Box>
                    </Box>

                    {item.type !== 'events' ? (
                      <>
                        <MuiLink
                          component={RouterLink as React.ElementType}
                          to={item?.url || '/'}
                          underline='none'
                          color={'#000'}
                        >
                          <Box
                            sx={{ height: '11rem', mb: '1rem' }}
                            //className='parentImg'
                          >
                            <img
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                              src={item?.image || 'fallback.jpg'}
                              alt='News'
                              className='carousel-image'
                            />
                          </Box>
                          <Typography className='titleNews'>
                            {item?.name}
                          </Typography>
                          <Typography className='subtitleNews'>
                            {item?.description}
                          </Typography>
                        </MuiLink>
                      </>
                    ) : (
                      <>
                        <Typography className='monthText'>{month}</Typography>
                        <Divider
                          sx={{ my: 1, borderColor: 'rgb(246, 143, 87)' }}
                        />

                        <Box
                          sx={{
                            height: '61%',
                            overflowY: 'scroll',
                            '&::-webkit-scrollbar': { width: '6px' },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: 'transparent',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: '#888',
                              borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                              backgroundColor: '#555',
                            },
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#888 transparent',
                          }}
                        >
                          {Object.entries(explore).map(([area, events]) => (
                            <Box
                              key={area}
                              className='eventInfo'
                              sx={{
                                '& .MuiBox-root': { mt: 0, mb: 0 },
                              }}
                            >
                              <Typography
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase',
                                }}
                                variant='subtitle1'
                              >
                                {area}
                              </Typography>
                              {events?.map((event: any, i: any) => (
                                <Box key={i} sx={{ ml: 2, my: 0.5 }}>
                                  <Typography
                                    variant='subtitle2'
                                    sx={{
                                      fontWeight: '600',
                                      lineHeight: '1.2rem',
                                    }}
                                  >
                                    <strong>{event.event}</strong> -{' '}
                                    {event.country}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ))}
                        </Box>

                        <Divider
                          sx={{
                            borderColor: 'rgb(246, 143, 87)',
                            mt: '0.5rem',
                          }}
                        />
                        <Typography
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                          }}
                          variant='subtitle1'
                        >
                          iGaming expo alerts?
                        </Typography>
                        <Button className='jumpInButton'>Jump In</Button>
                      </>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>
      </Grid2>
      <Box className='dots'>
        {data.map((_, i) => (
          <Box
            key={i}
            className={`dot ${i === startIndex ? 'active' : ''}`}
            onClick={() => {
              setStartIndex(i)
              if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = setInterval(() => {
                  setStartIndex(prev => (prev + 1) % data.length)
                }, SCROLL_INTERVAL)
              }
            }}
            style={{ cursor: 'pointer' }}
          />
        ))}
      </Box>
    </Box>
  )
}

export default News
